import React, { useEffect } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import DetailsTemplate from "screens/components/DetailsTemplate";
import {
  Toast,
  Input,
  Error,
  Button,
  Header,
  Loading,
  InputLabel,
  ParentCatPicker,
} from "components";
import Attrs from "./Attrs";

import { useForm, Controller } from "react-hook-form";

import {
  useCategory,
  useAddCategory,
  useEditCategory,
  useDeleteCategory,
} from "clients/apis";

import { colors, strings } from "assets/constants";
import { View } from "react-native";
import { useChmod } from "hooks";
import { alertPermission } from "utils";

interface FormDataT {
  title?: string;
  parent?: CategoryT;
  attrs: CategoryAttributeT[];
}

export default function Category() {
  const { chmod } = useChmod();

  const { id } = useRoute<RootRouteProps<"category">>().params;

  const headerTitle = id ? strings?.edit_cat : strings.add_cat;

  const { data, isError, error, refetch, isRefetching, isLoading } =
    useCategory(id);
  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormDataT>({
    defaultValues: {
      title: "",
      parent: undefined,
      attrs: [],
    },
  });

  useEffect(() => {
    if (data) {
      setValue("title", data?.data?.title);
      setValue("parent", data?.data?.parent);
      setValue("attrs", data?.data?.attributes);
    }
  }, [data, setValue]);

  const addCatMut = useAddCategory();
  const editCatMut = useEditCategory();
  const deleteCatMut = useDeleteCategory();

  const _onSubmit = () => {
    const { title, parent, attrs } = getValues();

    const postData: CategoryReqT = { title };
    parent?.id && Object.assign(postData, { parent_id: parent?.id });
    attrs &&
      attrs.length > 0 &&
      Object.assign(postData, {
        attributes: attrs.map((attr: CategoryAttributeT) => ({
          attribute_id: attr.id,
          items: attr.items?.map((item) => item.id),
          is_variable: attr.is_variable,
          price_ratio: Number(attr.price_ratio) || 0,
        })),
      });

    addCatMut.mutate(postData);
  };

  const _onEdit = () => {
    const { title, parent, attrs } = getValues();

    const postData: CategoryReqT = { title };
    parent?.id && Object.assign(postData, { parent_id: parent?.id });
    attrs &&
      attrs.length > 0 &&
      Object.assign(postData, {
        attributes: attrs.map((attr: CategoryAttributeT) => ({
          group_id: attr?.group_id || null,
          attribute_id: attr.id,
          items: attr.items?.map((item) => item.id),
          is_variable: attr.is_variable || 0,
          price_ratio: Number(attr.price_ratio) || 0,
        })),
      });

    editCatMut.mutate({ id: data?.data?.id, data: postData });
  };

  const _onDeleteConfirm = (close: () => void) =>
    deleteCatMut.mutate(data?.data.id, { onSuccess: close });

  const selectParentCatWarning = () =>
    Toast.warning({ message: strings.can_not_edit_parent });

  const _renderContent = () => {
    if (id && isLoading) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <DetailsTemplate
        onDelete={_onDeleteConfirm}
        deletable={data && chmod("category", "DELETE") ? true : false}
        deleteLoading={deleteCatMut?.isLoading}
        deleteDescription={strings.delete_category_description}
        submitLabel={!data ? strings.submit_info : strings.edit_info}
        submitLoading={addCatMut?.isLoading || editCatMut?.isLoading}
        onSubmit={
          data
            ? chmod("category", "PUT")
              ? handleSubmit(_onEdit)
              : alertPermission
            : chmod("category", "POST")
            ? handleSubmit(_onSubmit)
            : alertPermission
        }
      >
        <InputLabel label={strings.title} />
        <Controller
          control={control}
          rules={{ required: { value: true, message: strings.fill_input } }}
          name="title"
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChangeText={onChange}
              icon={{ name: "edit-3" }}
              placeholder={strings.insert_title}
              error={errors.title?.message}
            />
          )}
        />
        {data && !data?.data.parent ? null : (
          <>
            <InputLabel label={strings.parent_cat} />
            <Controller
              control={control}
              name="parent"
              render={({ field: { value, onChange } }) => (
                <View>
                  <ParentCatPicker
                    value={value}
                    onSelect={onChange}
                    onPress={data ? selectParentCatWarning : undefined}
                  />
                  {!data && getValues().parent !== undefined && (
                    <Button
                      onPress={() => setValue("parent", undefined)}
                      absolute
                      top={55}
                      width={"auto"}
                      height={25}
                      contentSeparator={0}
                      icon={{ name: "x", size: 13 }}
                      title={{
                        size: "sm",
                        color: colors.danger,
                        value: " " + strings.be_removed + " ",
                      }}
                      bgColor={colors.transparent}
                    />
                  )}
                </View>
              )}
            />
          </>
        )}
        <>
          <InputLabel label={strings.attributes} />
          <Controller
            control={control}
            name="attrs"
            render={({ field: { value, onChange } }) => (
              <Attrs attrs={value} onChange={onChange} />
            )}
          />
        </>
      </DetailsTemplate>
    );
  };

  return (
    <>
      <Header title={headerTitle} />
      {_renderContent()}
    </>
  );
}
